import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  BadgeCell,
  mapBooleanToBadgeVariant,
  RowActionsMenu,
  TextCell,
} from '../../../components/ui';

function ProfileIndexCell({ page, pageSize, rowIndex }) {
  const serial = page * pageSize + rowIndex + 1;
  return (
    <Typography
      component="span"
      sx={{
        fontSize: '13px',
        fontWeight: 500,
        fontVariantNumeric: 'tabular-nums',
        color: 'text.secondary',
      }}
    >
      {serial}
    </Typography>
  );
}

function ProfileNameCell({ name, accentColor }) {
  const label = name || '-';
  const initial = String(label).trim().charAt(0).toUpperCase() || '?';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '13px',
          fontWeight: 600,
          color: accentColor ?? '#2563EB',
          bgcolor: accentColor ? `${accentColor}14` : 'rgba(37, 99, 235, 0.08)',
        }}
      >
        {initial}
      </Box>
      <TextCell value={label} maxWidth="100%" fontWeight={600} />
    </Box>
  );
}

export function buildProfileRowActions({
  profile,
  onEdit,
  onPermissions,
  onClone,
  onDelete,
  canEdit = false,
  canManagePermissions = false,
  canClone = false,
  canDelete = false,
}) {
  const actions = [];

  if (canEdit) {
    actions.push({ id: 'edit', label: 'Edit', onClick: () => onEdit?.(profile) });
  }
  if (canManagePermissions) {
    actions.push({
      id: 'permissions',
      label: 'Permissions',
      onClick: () => onPermissions?.(profile),
    });
  }
  if (canClone) {
    actions.push({ id: 'clone', label: 'Clone Profile', onClick: () => onClone?.(profile) });
  }
  if (canDelete) {
    actions.push({
      id: 'delete',
      label: 'Delete',
      destructive: true,
      onClick: () => onDelete?.(profile),
    });
  }

  return actions;
}

export function useProfileTableColumns({
  page,
  pageSize,
  onEdit,
  onPermissions,
  onClone,
  onDelete,
  accentColor,
  canEdit = false,
  canManagePermissions = false,
  canClone = false,
  canDelete = false,
  showOrganisationColumn = false,
}) {
  return useMemo(
    () => {
      const columns = [
        {
          accessorKey: 'index',
          header: 'S.No.',
          enableSorting: false,
          size: 80,
          minSize: 72,
          maxSize: 88,
          Cell: ({ row }) => (
            <ProfileIndexCell page={page} pageSize={pageSize} rowIndex={row.index} />
          ),
        },
        {
          accessorKey: 'name',
          header: 'Profile Name',
          enableSorting: false,
          size: 300,
          Cell: ({ row }) => (
            <ProfileNameCell name={row.original.name} accentColor={accentColor} />
          ),
        },
      ];

      if (showOrganisationColumn) {
        columns.push({
          accessorKey: 'organisation.name',
          header: 'Organisation',
          enableSorting: false,
          size: 260,
          Cell: ({ row }) => (
            <TextCell value={row.original.organisation?.name} maxWidth="100%" muted />
          ),
        });
      }

      columns.push(
        {
          accessorKey: 'is_standard',
          header: 'Type',
          enableSorting: false,
          size: 140,
          minSize: 120,
          maxSize: 160,
          Cell: ({ row }) => {
            const badge = mapBooleanToBadgeVariant(row.original.is_standard, {
              trueLabel: 'Standard',
              falseLabel: 'Custom',
              trueVariant: 'success',
              falseVariant: 'neutral',
            });
            return <BadgeCell label={badge.label} variant={badge.variant} />;
          },
        },
        {
          accessorKey: 'actions',
          header: '',
          enableSorting: false,
          size: 56,
          minSize: 56,
          maxSize: 56,
          Cell: ({ row }) => {
            const actions = buildProfileRowActions({
              profile: row.original,
              onEdit,
              onPermissions,
              onClone,
              onDelete,
              canEdit,
              canManagePermissions,
              canClone,
              canDelete,
            });

            if (!actions.length) {
              return null;
            }

            return (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <RowActionsMenu actions={actions} />
              </Box>
            );
          },
        }
      );

      return columns;
    },
    [
      accentColor,
      canClone,
      canDelete,
      canEdit,
      canManagePermissions,
      onClone,
      onDelete,
      onEdit,
      onPermissions,
      page,
      pageSize,
      showOrganisationColumn,
    ]
  );
}
